import { useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Autocomplete,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormHelperText
} from '@mui/material';
import { Save, ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import AppLayout from '../../components/layout/AppLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import FormErrorSummary from '../../components/common/FormErrorSummary';
import tipologieService from '../../services/tipologieService';
import usersService from '../../services/usersService';
import type { CreateTipologiaDto, UpdateTipologiaDto } from '../../types/tipologia.types';
import type { User } from '../../types/user.types';
import { getErrorMessage } from '../../utils/api';

interface TipologiaFormData {
  nome: string;
  descrizione: string;
  colore: string;
  icona: string;
  intervalliReminder: number[];
  operatoriIds: string[];
}

const TipologiaFormPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!id;

  const { control, handleSubmit, reset, formState: { errors } } = useForm<TipologiaFormData>({
    defaultValues: {
      nome: '',
      descrizione: '',
      colore: '#1976d2',
      icona: '',
      intervalliReminder: [30],
      operatoriIds: []
    }
  });

  // Fetch tipologia if editing
  const { data: tipologia, isLoading: tipologiaLoading } = useQuery({
    queryKey: ['tipologia', id],
    queryFn: () => tipologieService.getById(id!),
    enabled: isEdit
  });

  // Fetch operatori
  const { data: operatori } = useQuery({
    queryKey: ['operatori'],
    queryFn: () => usersService.getOperatori()
  });

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: (data: CreateTipologiaDto | UpdateTipologiaDto) => {
      const submitData = {
        nome: data.nome,
        descrizione: data.descrizione,
        colore: data.colore,
        icona: data.icona,
        intervalliReminder: data.intervalliReminder
      };

      if (isEdit) {
        return tipologieService.update(id!, submitData as UpdateTipologiaDto);
      } else {
        return tipologieService.create(submitData as CreateTipologiaDto);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tipologie'] });
      enqueueSnackbar(
        isEdit ? 'Tipologia aggiornata con successo' : 'Tipologia creata con successo',
        { variant: 'success' }
      );
      navigate('/tipologie');
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante il salvataggio'), { variant: 'error' });
    }
  });

  useEffect(() => {
    if (tipologia) {
      reset({
        nome: tipologia.nome,
        descrizione: tipologia.descrizione || '',
        colore: tipologia.colore || '#1976d2',
        icona: tipologia.icona || '',
        intervalliReminder: tipologia.intervalliReminder || [30],
        operatoriIds: []
      });
    }
  }, [tipologia, reset]);

  const onSubmit = (data: TipologiaFormData) => {
    const submitData: CreateTipologiaDto | UpdateTipologiaDto = {
      nome: data.nome,
      descrizione: data.descrizione || undefined,
      colore: data.colore || undefined,
      icona: data.icona || undefined,
      intervalliReminder: data.intervalliReminder
    };

    saveMutation.mutate(submitData);
  };

  if (isEdit && tipologiaLoading) {
    return (
      <AppLayout>
        <LoadingSpinner message="Caricamento tipologia..." />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/tipologie')}
            variant="outlined"
          >
            Indietro
          </Button>
          <Typography variant="h4" component="h1" fontWeight={600}>
            {isEdit ? `Modifica Tipologia ${tipologia?.nome || ''}` : 'Nuova Tipologia'}
          </Typography>
        </Box>

        <Paper sx={{ p: 3 }}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            aria-label={isEdit ? 'Modifica tipologia scadenza' : 'Crea nuova tipologia scadenza'}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <FormErrorSummary errors={errors} />

              {/* Row 1: Nome, Colore */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
                <Controller
                  name="nome"
                  control={control}
                  rules={{ required: 'Nome obbligatorio' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Nome"
                      required
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  name="colore"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Colore"
                      type="color"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      helperText="Seleziona un colore per identificare la tipologia"
                    />
                  )}
                />
              </Box>

              {/* Row 2: Icona */}
              <Controller
                name="icona"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Icona"
                    fullWidth
                    placeholder="es. 📄, ⚠️, 📅"
                    helperText="Emoji o simbolo rappresentativo (opzionale)"
                  />
                )}
              />

              {/* Row 3: Giorni Preavviso (checkbox multipli) */}
              <Controller
                name="intervalliReminder"
                control={control}
                rules={{
                  validate: (value) => (value && value.length > 0) || 'Seleziona almeno un giorno di preavviso'
                }}
                render={({ field: { value, onChange }, fieldState }) => {
                  // Opzioni standard + opzione test 1 minuto solo in dev
                  const opzioniGiorni: { value: number; label: string }[] = [
                    { value: 60, label: '60 giorni' },
                    { value: 30, label: '30 giorni' },
                    { value: 15, label: '15 giorni' },
                    { value: 7, label: '7 giorni' },
                    { value: 5, label: '5 giorni' },
                    { value: 1, label: '1 giorno' },
                  ];

                  // Aggiungi opzione test 1 minuto solo in ambiente dev
                  if (import.meta.env.DEV) {
                    opzioniGiorni.push({ value: 0.0007, label: '1 minuto (TEST)' });
                  }

                  return (
                    <FormControl component="fieldset" error={!!fieldState.error}>
                      <FormLabel component="legend">Giorni Preavviso *</FormLabel>
                      <FormGroup row>
                        {opzioniGiorni.map(opzione => (
                          <FormControlLabel
                            key={opzione.value}
                            control={
                              <Checkbox
                                checked={value?.includes(opzione.value) || false}
                                onChange={(e) => {
                                  const currentValues = value || [];
                                  if (e.target.checked) {
                                    onChange([...currentValues, opzione.value].sort((a, b) => b - a));
                                  } else {
                                    onChange(currentValues.filter(g => g !== opzione.value));
                                  }
                                }}
                                name={`giorni-${opzione.value}`}
                                sx={opzione.value === 0.0007 ? { color: 'warning.main', '&.Mui-checked': { color: 'warning.main' } } : {}}
                              />
                            }
                            label={opzione.label}
                            sx={opzione.value === 0.0007 ? {
                              backgroundColor: 'warning.light',
                              borderRadius: 1,
                              px: 1,
                              '& .MuiFormControlLabel-label': { fontWeight: 'bold' }
                            } : {}}
                          />
                        ))}
                      </FormGroup>
                      <FormHelperText>
                        {fieldState.error?.message || 'Seleziona i giorni di preavviso per i reminder delle scadenze di questa tipologia'}
                      </FormHelperText>
                      {import.meta.env.DEV && (
                        <FormHelperText sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                          ⚠️ Ambiente DEV: opzione "1 minuto (TEST)" disponibile per testare il flusso notifiche
                        </FormHelperText>
                      )}
                    </FormControl>
                  );
                }}
              />

              {/* Row 4: Descrizione */}
              <Controller
                name="descrizione"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Descrizione"
                    multiline
                    rows={4}
                    fullWidth
                    placeholder="Inserisci una descrizione dettagliata della tipologia..."
                  />
                )}
              />

              {/* Row 5: Operatori Assegnati */}
              <Controller
                name="operatoriIds"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    multiple
                    options={operatori || []}
                    getOptionLabel={(option: User) =>
                      `${option.nome} ${option.cognome} (${option.email})`
                    }
                    value={operatori?.filter((o) => field.value.includes(o.id)) || []}
                    onChange={(_, newValue) => {
                      field.onChange(newValue.map((v) => v.id));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Operatori Assegnati"
                        helperText="Seleziona gli operatori che gestiranno questa tipologia"
                      />
                    )}
                  />
                )}
              />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/tipologie')}
                  disabled={saveMutation.isPending}
                >
                  Annulla
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Save />}
                  disabled={saveMutation.isPending}
                >
                  {isEdit ? 'Aggiorna' : 'Crea'} Tipologia
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </Box>
    </AppLayout>
  );
};

export default TipologiaFormPage;
